import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Building2, Monitor, Globe } from 'lucide-react';
import useOrgStore from '../../store/orgStore';
import { dialog } from '../../store/dialogStore';

const COLORS = [
  { label: 'Padrão (destaque)', value: null,      bg: 'var(--accent)' },
  { label: 'Branco',            value: '#FFFFFF',  bg: '#FFFFFF' },
  { label: 'Cinza claro',       value: '#CCCCCC',  bg: '#CCCCCC' },
  { label: 'Azul',              value: '#579bfc',  bg: '#579bfc' },
  { label: 'Verde',             value: '#00c875',  bg: '#00c875' },
  { label: 'Roxo',              value: '#a25ddc',  bg: '#a25ddc' },
  { label: 'Laranja',           value: '#fdab3d',  bg: '#fdab3d' },
  { label: 'Vermelho',          value: '#e2445c',  bg: '#e2445c' },
  { label: 'Preto',             value: '#111111',  bg: '#111111' },
];

export default function OrganizationPage() {
  const { org, fetchOrg, uploadLogo, deleteLogo, patchOrg, uploadLoginLogo, deleteLoginLogo, uploadFavicon, deleteFavicon } = useOrgStore();
  const fileRef = useRef(null);
  const loginFileRef = useRef(null);
  const faviconFileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [loginUploading, setLoginUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loginSaving, setLoginSaving] = useState(false);
  const [faviconMsg, setFaviconMsg] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loginMsg, setLoginMsg] = useState(null);
  const [headerName, setHeaderName] = useState('');
  const [headerColor, setHeaderColor] = useState(null);
  const [logoSize, setLogoSize] = useState(50);
  const [loginLogoSize, setLoginLogoSize] = useState(120);

  useEffect(() => {
    fetchOrg().catch(() => {});
  }, [fetchOrg]);

  useEffect(() => {
    setHeaderName(org?.header_name || '');
    setHeaderColor(org?.header_name_color || null);
    setLogoSize(org?.logo_size || 50);
    setLoginLogoSize(org?.login_logo_size || 120);
  }, [org?.header_name, org?.header_name_color, org?.logo_size, org?.login_logo_size]);

  const saveAppearance = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await patchOrg({ header_name: headerName, header_name_color: headerColor, logo_size: logoSize });
      setMsg({ type: 'ok', text: 'Aparência salva.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const saveLoginLogoSize = async () => {
    setLoginSaving(true);
    setLoginMsg(null);
    try {
      await patchOrg({ login_logo_size: loginLogoSize });
      setLoginMsg({ type: 'ok', text: 'Tamanho salvo.' });
    } catch (err) {
      setLoginMsg({ type: 'err', text: err.message });
    } finally {
      setLoginSaving(false);
    }
  };

  const pickFile = () => fileRef.current?.click();
  const pickLoginFile = () => loginFileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setMsg({ type: 'err', text: 'Envie um arquivo de imagem.' }); return; }
    if (file.size > 5 * 1024 * 1024) { setMsg({ type: 'err', text: 'A imagem deve ter até 5MB.' }); return; }
    setUploading(true);
    setMsg(null);
    try {
      await uploadLogo(file);
      setMsg({ type: 'ok', text: 'Logo atualizado.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const onLoginFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setLoginMsg({ type: 'err', text: 'Envie um arquivo de imagem.' }); return; }
    if (file.size > 10 * 1024 * 1024) { setLoginMsg({ type: 'err', text: 'A imagem deve ter até 10MB.' }); return; }
    setLoginUploading(true);
    setLoginMsg(null);
    try {
      await uploadLoginLogo(file);
      setLoginMsg({ type: 'ok', text: 'Logo de login atualizado.' });
    } catch (err) {
      setLoginMsg({ type: 'err', text: err.message });
    } finally {
      setLoginUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!org?.logo_url) return;
    if (!(await dialog.confirm('Remover logo', 'Remover o logo da organização?', { confirmLabel: 'Remover', danger: true }))) return;
    setMsg(null);
    try {
      await deleteLogo();
      setMsg({ type: 'ok', text: 'Logo removido.' });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    }
  };

  const removeLoginLogo = async () => {
    if (!org?.login_logo_url) return;
    if (!(await dialog.confirm('Remover logo', 'Remover o logo da tela de login?', { confirmLabel: 'Remover', danger: true }))) return;
    setLoginMsg(null);
    try {
      await deleteLoginLogo();
      setLoginMsg({ type: 'ok', text: 'Logo removido.' });
    } catch (err) {
      setLoginMsg({ type: 'err', text: err.message });
    }
  };

  const pickFaviconFile = () => faviconFileRef.current?.click();

  const onFaviconFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { setFaviconMsg({ type: 'err', text: 'Envie um arquivo de imagem.' }); return; }
    if (file.size > 1 * 1024 * 1024) { setFaviconMsg({ type: 'err', text: 'O favicon deve ter até 1MB.' }); return; }
    setFaviconUploading(true);
    setFaviconMsg(null);
    try {
      await uploadFavicon(file);
      setFaviconMsg({ type: 'ok', text: 'Favicon atualizado.' });
    } catch (err) {
      setFaviconMsg({ type: 'err', text: err.message });
    } finally {
      setFaviconUploading(false);
    }
  };

  const removeFavicon = async () => {
    if (!org?.favicon_url) return;
    if (!(await dialog.confirm('Remover favicon', 'Remover o favicon da organização?', { confirmLabel: 'Remover', danger: true }))) return;
    setFaviconMsg(null);
    try {
      await deleteFavicon();
      setFaviconMsg({ type: 'ok', text: 'Favicon removido.' });
    } catch (err) {
      setFaviconMsg({ type: 'err', text: err.message });
    }
  };

  return (
    <div className="p-8 max-w-2xl">
      <h2 className="text-2xl font-bold text-text-primary mb-6">Organização</h2>

      <div className="bg-card border border-border rounded-lg p-5 space-y-5">
        <h3 className="text-sm font-semibold text-text-primary">Identidade visual</h3>

        {/* Logo */}
        <div>
          <label className="block text-xs text-text-secondary mb-2">Logo</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-lg bg-main border border-border flex items-center justify-center overflow-hidden shrink-0">
              {org?.logo_url ? (
                <img src={org.logo_url} alt={org?.name} className="w-full h-full object-contain" />
              ) : (
                <Building2 className="w-8 h-8 text-text-secondary/60" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
              <button
                type="button"
                onClick={pickFile}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-accent/50 text-text-primary transition-colors disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Enviando...' : (org?.logo_url ? 'Trocar logo' : 'Enviar logo')}
              </button>
              {org?.logo_url && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-danger/50 text-text-secondary hover:text-danger transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remover
                </button>
              )}
              <p className="text-[11px] text-text-secondary/70">JPG, PNG, SVG ou GIF. Máximo 5MB.</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-4">
          {/* Logo size */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-text-secondary">Tamanho da logo</label>
              <span className="text-xs font-medium text-text-primary">{logoSize}px</span>
            </div>
            <input
              type="range"
              min="40"
              max="100"
              step="2"
              value={logoSize}
              onChange={(e) => setLogoSize(Number(e.target.value))}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-[10px] text-text-secondary/60 mt-0.5">
              <span>40px</span>
              <span>100px</span>
            </div>
          </div>

          {/* Header name */}
          <div>
            <label className="block text-xs text-text-secondary mb-1">Nome no cabeçalho</label>
            <p className="text-[11px] text-text-secondary/70 mb-2">Aparece ao lado do logo. Deixe em branco para não exibir texto.</p>
            <input
              type="text"
              value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveAppearance()}
              placeholder="Ex: Agência Arco"
              maxLength={120}
              className="w-full px-3 py-1.5 text-sm bg-main border border-border rounded-lg text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-accent/50"
            />
          </div>

          {/* Text color */}
          <div>
            <label className="block text-xs text-text-secondary mb-2">Cor do texto</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={String(c.value)}
                  type="button"
                  title={c.label}
                  onClick={() => setHeaderColor(c.value)}
                  className={`w-6 h-6 rounded-full border-2 transition-all shrink-0 ${
                    headerColor === c.value
                      ? 'border-white scale-110 shadow-md'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: c.bg }}
                />
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={saveAppearance}
              disabled={saving}
              className="px-4 py-1.5 text-xs bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando…' : 'Salvar aparência'}
            </button>
            {msg && (
              <span className={`text-xs ${msg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{msg.text}</span>
            )}
          </div>
        </div>
      </div>

      {/* Login logo section */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-5 mt-4">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-text-secondary" />
          <h3 className="text-sm font-semibold text-text-primary">Logo da tela de login</h3>
        </div>
        <p className="text-[11px] text-text-secondary/70 -mt-2">
          Logo exibida na tela de acesso. Aceita arquivos maiores para melhor qualidade. Se não definida, usa o logo padrão.
        </p>

        <div className="flex items-center gap-4">
          <div className="w-28 h-20 rounded-lg bg-main border border-border flex items-center justify-center overflow-hidden shrink-0">
            {org?.login_logo_url ? (
              <img src={org.login_logo_url} alt="Login logo" className="w-full h-full object-contain" />
            ) : org?.logo_url ? (
              <img src={org.logo_url} alt="Login logo (padrão)" className="w-full h-full object-contain opacity-40" />
            ) : (
              <Monitor className="w-8 h-8 text-text-secondary/30" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={loginFileRef} type="file" accept="image/*" onChange={onLoginFileChange} className="hidden" />
            <button
              type="button"
              onClick={pickLoginFile}
              disabled={loginUploading}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-accent/50 text-text-primary transition-colors disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" />
              {loginUploading ? 'Enviando...' : (org?.login_logo_url ? 'Trocar logo' : 'Enviar logo')}
            </button>
            {org?.login_logo_url && (
              <button
                type="button"
                onClick={removeLoginLogo}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-danger/50 text-text-secondary hover:text-danger transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remover
              </button>
            )}
            <p className="text-[11px] text-text-secondary/70">JPG, PNG, SVG ou GIF. Máximo 10MB.</p>
          </div>
        </div>

        <div className="border-t border-border pt-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-text-secondary">Tamanho na tela de login</label>
              <span className="text-xs font-medium text-text-primary">{loginLogoSize}px</span>
            </div>
            <input
              type="range"
              min="60"
              max="300"
              step="10"
              value={loginLogoSize}
              onChange={(e) => setLoginLogoSize(Number(e.target.value))}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-[10px] text-text-secondary/60 mt-0.5">
              <span>60px</span>
              <span>300px</span>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={saveLoginLogoSize}
              disabled={loginSaving}
              className="px-4 py-1.5 text-xs bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {loginSaving ? 'Salvando…' : 'Salvar tamanho'}
            </button>
            {loginMsg && (
              <span className={`text-xs ${loginMsg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{loginMsg.text}</span>
            )}
          </div>
        </div>
      </div>

      {/* Favicon section */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4 mt-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-text-secondary" />
          <h3 className="text-sm font-semibold text-text-primary">Favicon</h3>
        </div>
        <p className="text-[11px] text-text-secondary/70 -mt-2">
          Ícone exibido na aba do navegador. Recomendado: PNG ou ICO quadrado (32×32 ou 64×64px). Se não definido, usa o logo padrão.
        </p>

        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-main border border-border flex items-center justify-center overflow-hidden shrink-0">
            {org?.favicon_url ? (
              <img src={org.favicon_url} alt="Favicon" className="w-8 h-8 object-contain" />
            ) : org?.logo_url ? (
              <img src={org.logo_url} alt="Favicon (padrão)" className="w-8 h-8 object-contain opacity-40" />
            ) : (
              <Globe className="w-6 h-6 text-text-secondary/30" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={faviconFileRef} type="file" accept="image/*,.ico" onChange={onFaviconFileChange} className="hidden" />
            <button
              type="button"
              onClick={pickFaviconFile}
              disabled={faviconUploading}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-accent/50 text-text-primary transition-colors disabled:opacity-50"
            >
              <Upload className="w-3.5 h-3.5" />
              {faviconUploading ? 'Enviando...' : (org?.favicon_url ? 'Trocar favicon' : 'Enviar favicon')}
            </button>
            {org?.favicon_url && (
              <button
                type="button"
                onClick={removeFavicon}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-main border border-border rounded-lg hover:border-danger/50 text-text-secondary hover:text-danger transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remover
              </button>
            )}
            <p className="text-[11px] text-text-secondary/70">PNG, ICO ou SVG. Máximo 1MB.</p>
          </div>
        </div>

        {faviconMsg && (
          <span className={`text-xs ${faviconMsg.type === 'ok' ? 'text-success' : 'text-danger'}`}>{faviconMsg.text}</span>
        )}
      </div>
    </div>
  );
}
